import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useSubmitAttestation } from '@/hooks/api/useAttestations';

import { legalDocPlainText } from './attestationContent';
import { LegalDocArticle } from './LegalDocArticle';
import { getLegalDoc } from './legalContent';
import styles from './AttestationModal.module.css';

/**
 * Blocking sign-in attestation (PLA-1110): the AI Governance Policy.
 *
 * Shown on every login before any protected route renders. The policy is
 * rendered inline and a single "Accept and continue" records acceptance; no
 * checkbox, so the only work is reading. The only other exit is Sign out.
 * Full-screen sheet on phones, centered card from 640px up (shared Modal).
 */
interface AttestationModalProps {
  /** Called once the API has recorded the acceptance. */
  onAttested: () => void;
  onSignOut: () => void;
}

/**
 * The API refuses to record an attestation for an account with no provider
 * NPI (403 NPI_NOT_CONFIGURED). Retrying cannot fix that, so it gets its own
 * message pointing at the administrator instead of "try again".
 */
function isNpiNotConfigured(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 403) return false;
  const body = error.body as { error?: unknown } | undefined;
  return error.message === 'NPI_NOT_CONFIGURED' || body?.error === 'NPI_NOT_CONFIGURED';
}

export function AttestationModal({ onAttested, onSignOut }: AttestationModalProps) {
  const submit = useSubmitAttestation();
  const doc = getLegalDoc('ai');
  const npiMissing = submit.isError && isNpiNotConfigured(submit.error);

  const handleContinue = () => {
    if (submit.isPending || npiMissing || !doc) return;
    submit.mutate(
      { kind: 'ai_governance', text: legalDocPlainText(doc) },
      { onSuccess: onAttested },
    );
  };

  return (
    <Modal
      open
      blocking
      onClose={() => {}}
      labelledBy="attestation-title"
      fullScreenOnMobile
      title={
        <h2 id="attestation-title" className={styles.title}>
          {doc?.title ?? 'AI Disclosure'}
        </h2>
      }
      subtitle="Please review before continuing. This is shown each time you sign in."
      footer={
        <div className={styles.footer}>
          {submit.isError && (
            <div className={styles.alert} role="alert">
              {npiMissing
                ? 'Your account is not yet linked to a provider NPI, so your acceptance cannot be recorded. Please contact your administrator to have your NPI assigned, then sign in again.'
                : 'We couldn’t record your acceptance. Please try again.'}
            </div>
          )}

          <p className={styles.consent}>By continuing, you accept the AI Disclosure.</p>

          <div className={styles.actions}>
            <Button
              variant="ghost"
              onClick={onSignOut}
              disabled={submit.isPending}
              className={styles.secondary}
            >
              Sign out
            </Button>
            <Button
              size="lg"
              onClick={handleContinue}
              loading={submit.isPending}
              disabled={npiMissing}
              className={styles.primary}
            >
              Accept and continue
            </Button>
          </div>
        </div>
      }
    >
      {/* Focus lands on the policy itself so reading starts at the top. */}
      <div className={styles.content} tabIndex={-1} autoFocus>
        {doc && <LegalDocArticle doc={doc} />}
      </div>
    </Modal>
  );
}
